import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MinisterListTemplate } from "../templates";
import {
  useUserProfile,
  useMinisterList,
  useMinister,
  useMinisterForm,
} from "../hooks";
import {
  getAllDistrictsAndRegions,
  getDegreesList,
  type MinisterLinkedRecords,
} from "@/data/ministerRepository";
import { useAuth } from "@/context/authContext";
import type { Minister } from "@/models/minister";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { Degree } from "@/models/degree";
import { useToast } from "@/hooks/use-toast";
import { InlineLoader } from "@/components/ui/pulsing-loader";
import { AffiliationReceiptFlow } from "@/features/receipts/components/AffiliationReceiptFlow";

type DeleteDialogState = 'idle' | 'checking' | 'blocked' | 'ready';

/**
 * Page: Minister List
 * Orchestrates data loading and passes to template.
 * All filtering/sorting/pagination is handled inside MinistersTable.
 */
export function MinisterListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const { canEdit, isLoading: isLoadingUser } = useUserProfile();

  // Form dropdown data (districts, regions, degrees)
  const [districts, setDistricts] = useState<District[]>([]);
  const [allRegions, setAllRegions] = useState<Region[]>([]);
  const [degrees, setDegrees] = useState<Degree[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [districtsData, degreesData] = await Promise.all([
          getAllDistrictsAndRegions(),
          getDegreesList(),
        ]);
        setDistricts(districtsData);
        setDegrees(degreesData);
        setAllRegions(districtsData.flatMap((d) => d.regions || []));
      } catch (error) {
        console.error("Error loading form options:", error);
      }
    };
    load();
  }, []);

  const { ministers, isLoading, reload } = useMinisterList();

  const { createMinister, updateMinister, getMinisterLinkedRecords, hardDeleteMinister, isSubmitting, isDeleting, isCheckingLinks } =
    useMinister();

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingMinister, setEditingMinister] = useState<Minister | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingMinister, setViewingMinister] = useState<Minister | null>(null);
  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>('idle');
  const [deletingMinister, setDeletingMinister] = useState<Minister | null>(null);
  const [linkedRecords, setLinkedRecords] = useState<MinisterLinkedRecords | null>(null);
  // A just-created minister, to offer their Afiliación receipt (admins only; never automatic).
  const [affiliationMinister, setAffiliationMinister] = useState<Minister | null>(null);

  const showDeleteDialog = deleteDialogState !== 'idle';

  const ministerForm = useMinisterForm(editingMinister || undefined);

  const handleCreateNew = () => {
    setEditingMinister(null);
    ministerForm.reset();
    setShowFormDialog(true);
  };

  const handleView = (minister: Minister) => {
    setViewingMinister(minister);
    setShowViewDialog(true);
  };

  const handleEdit = (minister: Minister) => {
    navigate(`/ministers/${minister.id}`);
  };

  const handleDeleteClick = async (minister: Minister) => {
    setDeletingMinister(minister);
    setLinkedRecords(null);
    setDeleteDialogState('checking');
    const links = await getMinisterLinkedRecords(minister.id);
    if (!links) {
      setDeleteDialogState('idle');
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo verificar los registros vinculados.' });
      return;
    }
    setLinkedRecords(links);
    setDeleteDialogState(links.total > 0 ? 'blocked' : 'ready');
  };

  const handleFormSubmit = async () => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Usuario no autenticado",
        description: "Debe iniciar sesión para realizar esta acción.",
      });
      return;
    }

    const isValid = await ministerForm.validate();
    if (!isValid) {
      toast({
        variant: "destructive",
        title: "Errores en el formulario",
        description: "Por favor corrija los errores marcados antes de continuar.",
      });
      return;
    }

    let result: { minister: Minister | null; photoError?: string } | null = null;

    if (editingMinister) {
      result = await updateMinister(
        editingMinister.id,
        ministerForm.formData,
        user.id,
        ministerForm.processedPhotoFile,
        ministerForm.photoFile,
        editingMinister,
        ministerForm.shouldDeletePhoto
      );
    } else {
      result = await createMinister(
        ministerForm.formData,
        user.id,
        ministerForm.processedPhotoFile,
        ministerForm.photoFile
      );
    }

    if (result?.minister) {
      toast({
        title: editingMinister ? "Ministro actualizado" : "Ministro creado",
        description: `Ministro ${result.minister.adb_code} ${editingMinister ? "actualizado" : "creado"} exitosamente.`,
      });
      if (result.photoError) {
        toast({
          variant: "destructive",
          title: "Error al subir foto",
          description: result.photoError + " - Los datos del ministro se guardaron correctamente.",
        });
      }
      setShowFormDialog(false);
      if (!editingMinister && canEdit) setAffiliationMinister(result.minister);
      setEditingMinister(null);
      ministerForm.reset();
      reload();
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: `No se pudo ${editingMinister ? "actualizar" : "crear"} el ministro.`,
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingMinister) return;
    const success = await hardDeleteMinister(deletingMinister.id);
    if (success) {
      toast({
        title: "Ministro eliminado",
        description: `Ministro ${deletingMinister.adb_code} eliminado permanentemente.`,
      });
      setDeleteDialogState('idle');
      setDeletingMinister(null);
      setLinkedRecords(null);
      reload();
    } else {
      toast({
        variant: "destructive",
        title: "Error al eliminar",
        description: "No se pudo eliminar el ministro.",
      });
    }
  };

  const handleCloseDeleteDialog = () => {
    if (isDeleting || isCheckingLinks) return;
    setDeleteDialogState('idle');
    setDeletingMinister(null);
    setLinkedRecords(null);
  };

  if (isLoadingUser) return <InlineLoader />;

  return (
    <>
      <MinisterListTemplate
        ministers={ministers}
        districts={districts}
        allRegions={allRegions}
        degrees={degrees}
        isLoading={isLoading}
        canEdit={canEdit}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onCreateNew={handleCreateNew}
        showViewDialog={showViewDialog}
        onCloseViewDialog={() => { setShowViewDialog(false); setViewingMinister(null); }}
        viewingMinister={viewingMinister}
        showFormDialog={showFormDialog}
        onCloseFormDialog={() => { setShowFormDialog(false); setEditingMinister(null); ministerForm.reset(); }}
        formData={ministerForm.formData}
        formErrors={ministerForm.errors}
        formTouched={ministerForm.touched}
        onFormFieldChange={ministerForm.updateField}
        onFormFieldBlur={ministerForm.markFieldTouched}
        onFormSubmit={handleFormSubmit}
        photoPreview={ministerForm.photoPreview}
        onPhotoSelect={ministerForm.handlePhotoSelect}
        shouldDeletePhoto={ministerForm.shouldDeletePhoto}
        isSubmitting={isSubmitting}
        editingMinister={editingMinister}
        showDeleteDialog={showDeleteDialog}
        deleteDialogState={deleteDialogState}
        linkedRecords={linkedRecords}
        onCloseDeleteDialog={handleCloseDeleteDialog}
        onConfirmDelete={handleDeleteConfirm}
        deletingMinister={deletingMinister}
        isDeleting={isDeleting}
      />
      {affiliationMinister && (
        <AffiliationReceiptFlow
          minister={affiliationMinister}
          districtName={districts.find((d) => d.id === affiliationMinister.district_id)?.name}
          regionName={allRegions.find((r) => r.id === affiliationMinister.region_id)?.name}
          onClose={() => setAffiliationMinister(null)}
        />
      )}
    </>
  );
}
